import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RoadmapContext } from 'app/pages/AiAssistant/exposes/AIAssistant';

import { selectCardData } from 'app/components/SlideOutPanel/CommentPanel/CommentState/selectors';
import { selectHome } from 'app/pages/Home/slice/selectors';
import { useSlideoutUrl } from 'app/hooks/useSlideoutUrl';

type AssistantFilters = NonNullable<RoadmapContext['filters']>;

function buildSelectedFilters(
  homeState: ReturnType<typeof selectHome>
): RoadmapContext['filters'] | undefined {
  const f: Partial<AssistantFilters> = {};
  const put = <K extends keyof AssistantFilters>(
    key: K,
    value: string[] | undefined
  ) => {
    if (value?.length) {
      f[key] = value;
    }
  };

  put('productGroups', homeState.productGroups?.selectedGroups);
  put('audiences', homeState.audiences?.selectedAudiences);
  put('events', homeState.events?.selectedEvents);
  put('surfaces', homeState.features?.selectedFeatures);
  put('status', homeState.status?.selectedStatus);
  put('tiers', homeState.lanesTiers?.selectedLanesTiers);
  put('products', homeState.productFilter?.selectedProducts);
  put(
    'categoryProductCategories',
    homeState.categoryProductFilter?.selectedCategories
  );
  put(
    'categoryProductProducts',
    homeState.categoryProductFilter?.selectedProducts
  );
  put('genAiFilters', homeState.genAiFilter?.selectedFilters);
  put('revenueImpactFilters', homeState.revenueImpactFilter?.selectedFilters);
  put('momentTypeFilters', homeState.momentTypeFilter?.selectedMomentTypes);

  return Object.keys(f).length > 0 ? f : undefined;
}

export function useRoadmapAiAssistantContext(): RoadmapContext {
  const { pathname, search } = useLocation();
  const { isSlideoutOpen, currentIssueId } = useSlideoutUrl();
  const homeState = useSelector(selectHome);
  const cardData = useSelector(selectCardData);

  return useMemo(() => {
    const route = `${pathname}${search || ''}`;
    const out: RoadmapContext = { route };

    const start = homeState.selectedDates?.start;
    const end = homeState.selectedDates?.end;
    if (start && end) {
      out.dateRange = { start, end };
    }

    const filters = buildSelectedFilters(homeState);
    if (filters) {
      out.filters = filters;
    }

    if (isSlideoutOpen) {
      const issueId =
        cardData?.issueId?.trim() || currentIssueId?.trim() || undefined;
      if (issueId) {
        out.issueId = issueId;
      }
      const mt = cardData?.momentType?.trim();
      if (mt) {
        out.momentType = mt;
      }
    }

    return out;
  }, [pathname, search, homeState, cardData, isSlideoutOpen, currentIssueId]);
}
